import { format } from "date-fns";
import hexRgb from "hex-rgb";

const DATE_FORMAT = "dd.MM.yyyy, HH:mm";

export async function useFetch(url) {
  const data = [];
  const error = false;

  const resp = await fetch(url, {
    credentials: "include",
  });

  if (!resp.ok) {
    const errorResponse = await resp.json();
    const errorMsg =
      errorResponse?.errors?.[0]?.message ||
      errorResponse?.error ||
      "An unknown error occurred";
    throw new Error(errorMsg);
  }

  const rawData = await resp.json();
  let docs = rawData;
  //Account for Single or Mulitple Documents
  if (!rawData.id && !rawData.data) {
    docs = rawData.docs;
  }
  //  User fetch
  if (!docs) {
    docs = rawData.user;
  }
  // For Rainfocus
  if (docs && docs.responseMessage) {
    docs = docs.data[0];
  }

  return { data: docs, error };
}

export async function deleteFetch(url, dataObj) {
  return await doFetch(url, dataObj, "DELETE");
}

export async function patchFetch(url, dataObj) {
  return await doFetch(url, dataObj, "PATCH");
}

export async function postFetch(url, dataObj) {
  return await doFetch(url, dataObj, "POST");
}

export async function doFetch(url, dataObj, METHOD = "GET") {
  const error = false;

  const resp = await fetch(url, {
    method: METHOD,
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
    },
    ...(METHOD !== "GET" && { body: JSON.stringify(dataObj) }), // Only add `body` for non-GET requests
  });
  if (!resp.ok) {
    const errorMsg = (await resp.json())?.errors[0].message;
    throw new Error(errorMsg);
  }

  const rawData = await resp.json();
  let docs = rawData;
  //Account for Single or Mulitple Documents
  if (!rawData.id && !rawData.doc) {
    docs = rawData.docs;
  }

  if (rawData.doc) {
    docs = rawData.doc;
  }
  if (rawData.user) {
    docs = rawData.user;
  }

  return { data: docs, error: error };
}

export async function upload(url, dataObj) {
  const error = false;
  const resp = await fetch(url, {
    method: "POST",
    credentials: "include",
    body: dataObj,
  });

  if (!resp.ok) {
    const errorMsg = (await resp.json())?.errors[0].message;
    throw new Error(errorMsg);
  }

  const rawData = await resp.json();
  return { data: rawData, error: error };
}

export function formatDate(date, defaultValue = "—") {
  return date ? format(new Date(date), DATE_FORMAT) : defaultValue;
}

export function formatThemeColor(color) {
  const parts = hexRgb(color, { format: "array" });
  return parts.slice(0, -1).join(" ");
}

export function formatLengthUnit(value) {
  return Math.round(value * 2) / 2;
}

export async function wait(time) {
  await new Promise((resolve) => setTimeout(resolve, time));
}

export function getPathSegments(path, number) {
  const parts = path.split("/").filter(Boolean).slice(0, number);
  return parts.join("/");
}

export function getUniqueValues(array, prop) {
  const set = new Set();
  array.forEach((item) => {
    if (item[prop] !== undefined) {
      set.add(item[prop]);
    }
  });
  return Array.from(set);
}

export function addClickWithoutDragListener(element, callback, threshold = 10) {
  let isDragging = false;
  let startX, startY;

  element.addEventListener("mousedown", (event) => {
    isDragging = false;
    startX = event.clientX;
    startY = event.clientY;

    const mouseMoveHandler = (event) => {
      const deltaX = Math.abs(event.clientX - startX);
      const deltaY = Math.abs(event.clientY - startY);
      if (deltaX > threshold || deltaY > threshold) {
        isDragging = true;
      }
    };

    const mouseUpHandler = () => {
      element.removeEventListener("mousemove", mouseMoveHandler);
      element.removeEventListener("mouseup", mouseUpHandler);
      if (!isDragging) {
        callback();
      }
    };

    element.addEventListener("mousemove", mouseMoveHandler);
    element.addEventListener("mouseup", mouseUpHandler);
  });
}

export function addDragWithoutClickListener(
  element,
  startCallback,
  endCallback,
  pixelThreshold = 10,
) {
  let isDragging = false;
  let startX, startY;

  element.addEventListener("mousedown", (event) => {
    isDragging = false;
    startX = event.clientX;
    startY = event.clientY;

    const mouseMoveHandler = (event) => {
      const deltaX = Math.abs(event.clientX - startX);
      const deltaY = Math.abs(event.clientY - startY);
      if (deltaX > pixelThreshold || deltaY > pixelThreshold) {
        isDragging = true;
        startCallback();
      }
    };

    const mouseUpHandler = () => {
      element.removeEventListener("mousemove", mouseMoveHandler);
      element.removeEventListener("mouseup", mouseUpHandler);
      if (isDragging) {
        endCallback();
      }
    };

    element.addEventListener("mousemove", mouseMoveHandler);
    element.addEventListener("mouseup", mouseUpHandler);
  });
}

export function adjustHexColor(hex, saturationAmount, lightnessAmount) {
  if (!hex) return;
  // Ensure 'saturationAmount' and 'lightnessAmount' are within the valid range (-1 to 1)
  saturationAmount = Math.min(1, Math.max(-1, saturationAmount));
  lightnessAmount = Math.min(1, Math.max(-1, lightnessAmount));

  // Parse the hex color into RGB components
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);

  // Convert RGB to HSL
  let hsl = rgbToHsl(r, g, b);

  // Adjust saturation and lightness
  hsl[1] += saturationAmount;
  hsl[2] += lightnessAmount;

  // Clamp the values between 0 and 1
  hsl[1] = Math.min(1, Math.max(0, hsl[1]));
  hsl[2] = Math.min(1, Math.max(0, hsl[2]));

  // Convert HSL back to RGB
  let rgb = hslToRgb(hsl[0], hsl[1], hsl[2]);

  // Convert RGB components back to hex
  const adjustedHex = rgbToHex(rgb[0], rgb[1], rgb[2]);

  return adjustedHex;
}

function rgbToHsl(r, g, b) {
  r /= 255;
  g /= 255;
  b /= 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);

  let h,
    s,
    l = (max + min) / 2;

  if (max === min) {
    h = s = 0; // achromatic
  } else {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r:
        h = (g - b) / d + (g < b ? 6 : 0);
        break;
      case g:
        h = (b - r) / d + 2;
        break;
      case b:
        h = (r - g) / d + 4;
        break;
    }
    h /= 6;
  }

  return [h, s, l];
}

function hslToRgb(h, s, l) {
  let r, g, b;

  if (s === 0) {
    r = g = b = l; // achromatic
  } else {
    const hue2rgb = (p, q, t) => {
      if (t < 0) t += 1;
      if (t > 1) t -= 1;
      if (t < 1 / 6) return p + (q - p) * 6 * t;
      if (t < 1 / 2) return q;
      if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
      return p;
    };

    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    r = hue2rgb(p, q, h + 1 / 3);
    g = hue2rgb(p, q, h);
    b = hue2rgb(p, q, h - 1 / 3);
  }

  return [Math.round(r * 255), Math.round(g * 255), Math.round(b * 255)];
}

function rgbToHex(r, g, b) {
  return `#${((1 << 24) | (r << 16) | (g << 8) | b).toString(16).slice(1)}`;
}
